import PropTypes from 'prop-types';
import React, { Children, memo, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { Map } from 'immutable';

import { Spinner } from './Spinner';

/**
 * Table row. Can display the default value if no key is specified on this row.
 */
const TableRow = memo(
  // Props used only by the memo comparator (canEdit is compared via checkAlso) must not reach the DOM <tr>.
  ({ data, fields, defaultValues, isEditing, index, shouldRowUpdate, checkAlso, canEdit, onCellClick, ...rest }) => {
    const handleCellClick = useCallback(
      fieldName => {
        if (onCellClick) onCellClick(fieldName);
      },
      [onCellClick],
    );

    const cells = fields.map((field, i) => {
      const fieldValue = data.getIn(field.name.split('.'), defaultValues[i]);
      const value = typeof field.valueWrapper === 'function' ? field.valueWrapper(fieldValue, data, index) : fieldValue;
      const cellProps = onCellClick ? { onClick: () => handleCellClick(field.name) } : {};

      return (
        <td key={i} {...cellProps}>
          {value}
        </td>
      );
    });

    return <tr {...rest}>{cells}</tr>;
  },
  (prevProps, nextProps) => {
    if (nextProps.shouldRowUpdate) {
      return !nextProps.shouldRowUpdate(prevProps, nextProps);
    }

    return (
      prevProps.data === nextProps.data &&
      prevProps.isEditing === nextProps.isEditing &&
      !(prevProps.checkAlso || []).some(field => nextProps[field] !== prevProps[field])
    );
  },
);

TableRow.propTypes = {
  data: PropTypes.instanceOf(Map).isRequired,
  defaultValues: PropTypes.arrayOf(PropTypes.string).isRequired,
  fields: PropTypes.arrayOf(PropTypes.object).isRequired,
  isEditing: PropTypes.bool,
};

/**
 * A Column component. Has no real render. It is using to definition of table's columns.
 */
const Column = () => null;

// field, defaultValue and valueWrapper describe the column data, they must not reach the DOM <th>.
export const RealColumn = memo(({ title, children, field, defaultValue, valueWrapper, ...rest }) => (
  <th {...rest}>{children || title}</th>
));

/**
 * A Row component. Has no real render. It is using to definition of table's rows.
 */
const Row = () => {};

Row.propTypes = {
  data: PropTypes.instanceOf(Map).isRequired,
  onDoubleClick: PropTypes.func,
};

/**
 * Table component.
 */
const TableComponent = ({
  children,
  className = 'table table-bordered',
  bodyClassName,
  loading,
  shouldRowUpdate,
  ...rest
}) => {
  const { fields, columns, rows } = useMemo(() => {
    const defaultValues = [];
    const fields = [];
    const columns = [];
    const rows = [];

    Children.forEach(children, (child, i) => {
      switch (child.type) {
        case Column: {
          const { field, defaultValue = '', valueWrapper } = child.props;

          fields.push({ name: field, valueWrapper });
          defaultValues.push(defaultValue);
          columns.push(<RealColumn {...child.props} key={i} />);
          break;
        }

        case Row: {
          const { children: rowChildren, ...rowRest } = child.props;

          rows.push(
            <TableRow
              {...rowRest}
              key={child.key}
              defaultValues={defaultValues}
              fields={fields}
              index={rows.length}
              shouldRowUpdate={shouldRowUpdate}
            >
              {rowChildren}
            </TableRow>,
          );
          break;
        }

        default:
          break;
      }
    });

    return { fields, columns, rows };
  }, [children, shouldRowUpdate]);

  const isEmpty = rows.length === 0;
  const displayRows = isEmpty
    ? [
        <tr key="noRows" className="empty-row">
          <td className="text-center" colSpan={fields.length}>
            No rows to display
          </td>
        </tr>,
      ]
    : rows;

  const spinner = loading ? (
    <tr className="loading-row">
      <td colSpan={fields.length}>
        <Spinner className="text-primary" inline />
      </td>
    </tr>
  ) : null;

  return (
    <table {...rest} className={`table ${classNames(className, { 'empty-table': isEmpty })}`}>
      <thead>
        <tr>{columns}</tr>
      </thead>
      <tbody className={bodyClassName}>
        {displayRows}
        {spinner}
      </tbody>
    </table>
  );
};

TableComponent.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

export { Column, Row };
export default memo(TableComponent);
